import { add } from 'date-fns/fp/add';
import { addDays } from 'date-fns/fp/addDays';
import { addMonths } from 'date-fns/fp/addMonths';
import { addYears } from 'date-fns/fp/addYears';
import { differenceInCalendarDays } from 'date-fns/fp/differenceInCalendarDays';
import { differenceInDays } from 'date-fns/fp/differenceInDays';
import { differenceInHours } from 'date-fns/fp/differenceInHours';
import { differenceInMilliseconds } from 'date-fns/fp/differenceInMilliseconds';
import { differenceInSeconds } from 'date-fns/fp/differenceInSeconds';
import { endOfDay } from 'date-fns/fp/endOfDay';
import { endOfMinute } from 'date-fns/fp/endOfMinute';
import { endOfMonth } from 'date-fns/fp/endOfMonth';
import { endOfWeek } from 'date-fns/fp/endOfWeek';
import { format } from 'date-fns/fp/format';
import { formatDistanceStrict } from 'date-fns/fp/formatDistanceStrict';
import { formatDistanceWithOptions } from 'date-fns/fp/formatDistanceWithOptions';
import { formatDuration } from 'date-fns/fp/formatDuration';
import { formatDurationWithOptions } from 'date-fns/fp/formatDurationWithOptions';
import { formatRelativeWithOptions } from 'date-fns/fp/formatRelativeWithOptions';
import { formatWithOptions } from 'date-fns/fp/formatWithOptions';
import { getDate } from 'date-fns/fp/getDate';
import { getDay } from 'date-fns/fp/getDay';
import { getHours } from 'date-fns/fp/getHours';
import { getMilliseconds } from 'date-fns/fp/getMilliseconds';
import { getMinutes } from 'date-fns/fp/getMinutes';
import { getMonth } from 'date-fns/fp/getMonth';
import { getSeconds } from 'date-fns/fp/getSeconds';
import { getWeekWithOptions } from 'date-fns/fp/getWeekWithOptions';
import { getYear } from 'date-fns/fp/getYear';
import { interval } from 'date-fns/fp/interval';
import { intervalToDuration } from 'date-fns/fp/intervalToDuration';
import { isAfter } from 'date-fns/fp/isAfter';
import { isBefore } from 'date-fns/fp/isBefore';
import { isDate as isDate_ } from 'date-fns/fp/isDate';
import { isEqual } from 'date-fns/fp/isEqual';
import { isMonday } from 'date-fns/fp/isMonday';
import { isSameDay } from 'date-fns/fp/isSameDay';
import { isSameHour } from 'date-fns/fp/isSameHour';
import { isValid } from 'date-fns/fp/isValid';
import { isWeekend } from 'date-fns/fp/isWeekend';
import { isWithinInterval } from 'date-fns/fp/isWithinInterval';
import { milliseconds } from 'date-fns/fp/milliseconds';
import { millisecondsToSeconds } from 'date-fns/fp/millisecondsToSeconds';
import { nextMonday } from 'date-fns/fp/nextMonday';
import { nextTuesday } from 'date-fns/fp/nextTuesday';
import { nextWednesday } from 'date-fns/fp/nextWednesday';
import { parseWithOptions } from 'date-fns/fp/parseWithOptions';
import { previousMonday } from 'date-fns/fp/previousMonday';
import { previousTuesday } from 'date-fns/fp/previousTuesday';
import { previousWednesday } from 'date-fns/fp/previousWednesday';
import { set } from 'date-fns/fp/set';
import { setDate } from 'date-fns/fp/setDate';
import { setDay } from 'date-fns/fp/setDay';
import { setHours } from 'date-fns/fp/setHours';
import { setMilliseconds } from 'date-fns/fp/setMilliseconds';
import { setMinutes } from 'date-fns/fp/setMinutes';
import { setMonth } from 'date-fns/fp/setMonth';
import { setSeconds } from 'date-fns/fp/setSeconds';
import { setYear } from 'date-fns/fp/setYear';
import { startOfDay } from 'date-fns/fp/startOfDay';
import { startOfMinute } from 'date-fns/fp/startOfMinute';
import { startOfWeek } from 'date-fns/fp/startOfWeek';
import { startOfWeekWithOptions } from 'date-fns/fp/startOfWeekWithOptions';
import { sub } from 'date-fns/fp/sub';
import { enUS } from 'date-fns/locale/en-US';
import { array, date, eq, io, number } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import * as ord from './ord.js';
export * from 'fp-ts/Date';
export { add, addDays, addMonths, addYears, differenceInCalendarDays, differenceInDays, differenceInHours, differenceInMilliseconds, differenceInSeconds, endOfDay, endOfMinute, endOfMonth, endOfWeek, enUS, format, formatDistanceStrict, formatDistanceWithOptions, formatDuration, formatDurationWithOptions, formatRelativeWithOptions, formatWithOptions, getDate, getDay, getHours, getMilliseconds, getMinutes, getMonth, getSeconds, getWeekWithOptions, getYear, interval, intervalToDuration, isAfter, isBefore, isEqual, isMonday, isSameDay, isSameHour, isValid, isWeekend, isWithinInterval, milliseconds, millisecondsToSeconds, nextMonday, nextTuesday, nextWednesday, parseWithOptions, previousMonday, previousTuesday, previousWednesday, set, setDate, setDay, setHours, setMilliseconds, setMinutes, setMonth, setSeconds, setYear, startOfDay, startOfMinute, startOfWeek, startOfWeekWithOptions, sub, };
/**
 * Check whether a date is one or more calendar days after the given base date.
 */
export const isAfterCalendarDay = (base) => flow(differenceInCalendarDays(base), (diff) => diff > 0);
/**
 * Check whether a date is one or more calendar days before the given base date.
 */
export const isBeforeCalendarDay = (base) => flow(differenceInCalendarDays(base), (diff) => diff < 0);
/**
 * Check whether a date is between the given start and end hours (inclusive).
 */
export const isWithinTimeOfDayInterval = (minHours, maxHours) => (x) => {
    const start = set({ hours: minHours, minutes: 0, seconds: 0, milliseconds: 0 })(x);
    const end = set({ hours: maxHours, minutes: 0, seconds: 0, milliseconds: 0 })(x);
    return isWithinInterval({ start, end })(x);
};
/**
 * Check if a value is a Date.
 */
export const isDate = (x) => isDate_(x);
/**
 * Create a date at the start of the current day.
 */
export const atStartOfDay = pipe(date.create, io.map(startOfDay));
/**
 * Create a date at the end of the current day.
 */
export const atEndOfDay = pipe(date.create, io.map(endOfDay));
/**
 * Make a date precise to the minute, stripping out seconds and below.
 */
export const precisionMinute = set({ seconds: 0, milliseconds: 0 });
export const getSignificantElements = (precision) => (duration) => {
    const elements = [
        'years',
        'months',
        'weeks',
        'days',
        'hours',
        'minutes',
        'seconds',
    ];
    return pipe(elements, array.dropLeftWhile((e) => {
        const value = duration[e];
        return value == null || value == 0;
    }), array.takeLeft(precision));
};
/**
 * Given two dates, return the date that comes later in time.
 */
export const max = ord.max(date.Ord);
const intervalToMilliseconds = ({ start, end }) => {
    const { start: startDate, end: endDate } = interval(start, end);
    return endDate.getTime() - startDate.getTime();
};
export const ordInterval = ord.contramap(intervalToMilliseconds)(number.Ord);
export const ordNullable = ord.getNullable(date.Ord);
export const ordStartOfMinute = pipe(date.Ord, ord.contramap(precisionMinute));
/**
 * Given a date, set its year, month and day of month to those of the given source date.
 */
export const setCalendarDay = (source) => set({
    year: source.getFullYear(),
    month: source.getMonth(),
    date: source.getDate(),
});
/**
 * Given a date, set its hours, minutes, seconds and milliseconds to those of the given source date.
 */
export const setTimeOfDay = (source) => set({
    hours: source.getHours(),
    minutes: source.getMinutes(),
    seconds: source.getSeconds(),
    milliseconds: source.getMilliseconds(),
});
export const eqNormalizedInterval = eq.struct({
    start: date.Eq,
    end: date.Eq,
});
export const seconds = flow(milliseconds, (ms) => ms / 1000);
//# sourceMappingURL=date.js.map